import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { allQuests } from "@/mocks/artistData";
import Navbar from "@/pages/home/components/Navbar";
import QuestCard from "./components/QuestCard";
import QuestFilters from "./components/QuestFilters";
import type { SortOption, DifficultyFilter } from "./components/QuestFilters";

const deadlineDays = (deadline: string): number => {
  const match = deadline.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 99;
};

export default function QuestsPage() {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedArtist, setSelectedArtist] = useState(() => searchParams.get("artist") ?? "all");
  const [selectedDifficulty, setSelectedDifficulty] = useState<DifficultyFilter>("all");
  const [sortBy, setSortBy] = useState<SortOption>("latest");

  // URL 파라미터 변경 시 아티스트 필터 동기화
  useEffect(() => {
    const artistParam = searchParams.get("artist");
    if (artistParam) {
      setSelectedArtist(artistParam);
    }
  }, [searchParams]);

  const filtered = useMemo(() => {
    let result = [...allQuests];

    if (selectedArtist !== "all") {
      result = result.filter((q) => q.artistId === selectedArtist);
    }
    if (selectedDifficulty !== "all") {
      result = result.filter((q) => q.difficulty === selectedDifficulty);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (quest) =>
          quest.title.toLowerCase().includes(q) ||
          quest.description.toLowerCase().includes(q) ||
          quest.tag.toLowerCase().includes(q)
      );
    }

    switch (sortBy) {
      case "latest":
        result = result.sort((a, b) => parseInt(b.id.split("-")[1]) - parseInt(a.id.split("-")[1]));
        break;
      case "popular":
        result = result.sort((a, b) => b.participants - a.participants);
        break;
      case "reward":
        result = result.sort((a, b) => b.reward - a.reward);
        break;
      case "deadline":
        result = result.sort((a, b) => deadlineDays(a.deadline) - deadlineDays(b.deadline));
        break;
    }

    return result;
  }, [searchQuery, selectedArtist, selectedDifficulty, sortBy]);



  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />
      {/* Hero Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9784f79ec7b9e68b81bdf4bf9a05ea98.jpg"
            alt="Quest Hero"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/60 via-[#0a0a0f]/40 to-[#0a0a0f]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-16">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-white/40 mb-6">
            <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">홈</Link>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs" />
            </div>
            <span className="text-white/70">캠페인</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#00f5d4]/20 to-[#f72585]/20 border border-white/10">
                  <i className="ri-sword-line text-xl text-[#00f5d4]" />
                </div>
                <span className="text-[#00f5d4] text-sm font-semibold tracking-widest uppercase">All Campaigns</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-white leading-tight">
                모든 아티스트의
                <br />
                <span className="bg-gradient-to-r from-[#00f5d4] via-[#f72585] to-[#ffd60a] bg-clip-text text-transparent">
                  캠페인 탐험
                </span>
              </h1>
              <p className="text-white/50 mt-3 text-base max-w-xl">
                5명의 아티스트가 제공하는 {allQuests.length}개의 캠페인에 도전하고 AI 영상을 제작하세요.
              </p>
            </div>


          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-20 space-y-10">
        {/* Filters */}
        <div className="bg-[#12121a] border border-white/10 rounded-2xl px-5 py-4">
          <QuestFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedArtist={selectedArtist}
            onArtistChange={setSelectedArtist}
            selectedDifficulty={selectedDifficulty}
            onDifficultyChange={setSelectedDifficulty}
            sortBy={sortBy}
            onSortChange={setSortBy}
            totalCount={filtered.length}
          />
        </div>

        {/* Quest Grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((quest) => (
              <QuestCard key={quest.id} quest={quest} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10">
              <i className="ri-search-line text-3xl text-white/20" />
            </div>
            <p className="text-white/40 text-base">검색 결과가 없습니다</p>
            <button
              onClick={() => {
                setSearchQuery("");
                setSelectedArtist("all");
                setSelectedDifficulty("all");
              }}
              className="px-5 py-2 rounded-full border border-white/20 text-white/60 text-sm hover:border-white/40 hover:text-white/80 cursor-pointer transition-all whitespace-nowrap"
            >
              필터 초기화
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
